import React, { useState, useEffect, useCallback } from "react"
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  CircularProgress
} from "@mui/material"
import { useNavigate } from "react-router-dom"
import { Capacitor } from "@capacitor/core"
import { Preferences } from "@capacitor/preferences"
import {
  login,
  recoverPassword,
  existsInCliente,
  existsInSocorrista,
  existsInAdministrador,
  getClienteByUserId,
  getSocorristaByUserId,
  getAdministradorByUserId,
  existsInFamiliar,
  getFamiliarByUserId,
  existsInMedicos,
  getMedicoByUserId,
  existsInDependente,
  getDependenteByUserId
} from "./api"
import Logo from "../../assets/cropped_image.png"
import SplashScreen from "./SplashScreen"
import { Browser } from '@capacitor/browser';

const Login: React.FC = () => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showLogin, setShowLogin] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showSplash, setShowSplash] = useState(true)
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const navigate = useNavigate()

  // Função para verificar se o usuário está ativo
  const isUserActive = (user: any, userType: string): boolean => {
    if (!user) return false

    // Verifica diferentes campos de status possíveis
    const status = user.status || user.ativo || user.ativado || user.active

    // Converte para string e normaliza para comparação
    const statusStr = String(status).toLowerCase().trim()

    // Status considerados como ativo
    const activeStatuses = [
      "true",
      "1",
      "ativo",
      "ativado",
      "active",
      "enabled",
      "habilitado"
    ]

    const isActive = activeStatuses.includes(statusStr)

    console.log(`[Login] Verificando status do ${userType}:`, {
      status: status,
      statusStr: statusStr,
      isActive: isActive
    })

    return isActive
  }

  // Função para sincronizar Preferences com localStorage
  const syncPreferencesToLocalStorage = useCallback(async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        console.log(
          "[Login] Sincronizando dados do Capacitor Preferences para localStorage..."
        )

        // Lista de chaves que precisamos sincronizar
        const keysToSync = [
          "accessToken",
          "refreshToken",
          "userToken",
          "userId",
          "userEmail",
          "userType",
          "clienteId",
          "socorristaId",
          "administradorId",
          "familiarId",
          "medicoId",
          "dependenteId"
        ]

        let syncedKeys = 0

        // Verifica cada chave no Preferences
        for (const key of keysToSync) {
          const { value } = await Preferences.get({ key })
          if (value) {
            // Se encontrou valor no Preferences, coloca no localStorage
            localStorage.setItem(key, value)
            console.log(
              `[Login] Sincronizado ${key}: ${
                key.includes("Token") || key.includes("Id") ? "[HIDDEN]" : value
              }`
            )
            syncedKeys++
          }
        }

        console.log(
          `[Login] Total de chaves sincronizadas: ${syncedKeys}/${keysToSync.length}`
        )

        // Agora verifica se tem dados suficientes para autenticar
        const accessToken =
          localStorage.getItem("accessToken") ||
          localStorage.getItem("userToken")
        const userId = localStorage.getItem("userId")

        if (!accessToken || !userId) {
          console.log(
            "[Login] Dados insuficientes para autenticação automática"
          )
          await clearAllStorage()
          return false
        }

        console.log(
          "[Login] Dados suficientes encontrados para autenticação automática"
        )
        return true
      } catch (error) {
        console.error("[Login] Erro ao sincronizar Preferences:", error)
        return false
      }
    }
    return false
  }, [])

  // Função para limpar todos os storages
  const clearAllStorage = async () => {
    console.log("[Login] Limpando todos os storages...")

    // Limpa localStorage
    localStorage.clear()

    // Se estiver no mobile, limpa também o Preferences
    if (Capacitor.isNativePlatform()) {
      try {
        await Preferences.clear()
        console.log("[Login] Preferences limpo com sucesso")
      } catch (error) {
        console.error("[Login] Erro ao limpar Preferences:", error)
      }
    }
  }

  // Função auxiliar para salvar em ambos os storages
  const saveToStorage = async (key: string, value: string) => {
    console.log(
      `[Login] Salvando ${key}: ${
        key.includes("Token") || key.includes("Id") || key.includes("Email")
          ? "[HIDDEN]"
          : value
      }`
    )

    // Salva no localStorage
    localStorage.setItem(key, value)

    // Se estiver no mobile, salva também no Preferences
    if (Capacitor.isNativePlatform()) {
      try {
        await Preferences.set({ key, value })
        console.log(`[Login] ${key} salvo no Capacitor Preferences`)
      } catch (error) {
        console.error(`[Login] Erro ao salvar ${key} no Preferences:`, error)
      }
    }
  }

  // Redirecionamento automático se já estiver autenticado
  useEffect(() => {
    const checkAuthAndSync = async () => {
      // Verificar se há fragmentos de URL de autenticação do Supabase (convite/recuperação)
      const hash = window.location.hash;
      if (hash && (hash.includes('type=invite') || hash.includes('type=recovery') || hash.includes('access_token'))) {
        console.log("[Login] Detectado fragmento de autenticação Supabase (convite/recuperação). Redirecionando para definição de senha...");
        // Importante: NÃO limpar o storage aqui, pois o cliente Supabase pode ter acabado de salvar o token da sessão
        navigate("/reset-password");
        return;
      }

      console.log(
        `[Login] Verificando autenticação. Plataforma: ${
          Capacitor.isNativePlatform() ? "Mobile Nativo" : "Web"
        }`
      )

      // Se estiver no mobile, tenta sincronizar primeiro
      if (Capacitor.isNativePlatform()) {
        const hasSyncedData = await syncPreferencesToLocalStorage()
        if (!hasSyncedData) {
          console.log("[Login] Sincronização falhou ou dados insuficientes")
          setIsCheckingAuth(false)
          return // Se não conseguiu sincronizar, não tenta redirecionar
        }
      }

      // A partir daqui usa o localStorage normalmente
      const accessToken =
        localStorage.getItem("accessToken") || localStorage.getItem("userToken")
      const userId = localStorage.getItem("userId")

      if (!accessToken || !userId) {
        console.log("[Login] Credenciais não encontradas, limpando storages")
        await clearAllStorage()
        setIsCheckingAuth(false)
        return
      }

      console.log(
        "[Login] Credenciais encontradas, verificando tipo de usuário..."
      )

      // Verificar e redirecionar baseado no tipo de usuário
      if (localStorage.getItem("clienteId")) {
          console.log("[Login] Redirecionando cliente autenticado para /home")
          navigate("/home", { replace: true })
      } else if (localStorage.getItem("socorristaId")) {
        console.log(
          "[Login] Redirecionando socorrista autenticado para /partner-emergencies"
        )
        navigate("/partner-emergencies", { replace: true })
      } else if (localStorage.getItem("administradorId")) {
        console.log(
          "[Login] Redirecionando administrador autenticado para /admin-panel"
        )
        navigate("/admin-panel", { replace: true })
      } else if (localStorage.getItem("familiarId")) {
        console.log(
          "[Login] Redirecionando familiar autenticado para /family-emergencies"
        )
        navigate("/family-emergencies", { replace: true })
      } else if (localStorage.getItem("medicoId")) {
        console.log(
          "[Login] Redirecionando médico autenticado para /telemedicina/medico"
        )
        navigate("/telemedicina/medico", { replace: true })
      } else if (localStorage.getItem("dependenteId")) {
        console.log("[Login] Redirecionando dependente autenticado para /carteirinha")
        navigate("/carteirinha", { replace: true })
      } else {
        console.log(
          "[Login] Tipo de usuário não identificado, limpando storages"
        )
        await clearAllStorage()
        setIsCheckingAuth(false)
      }
    }

    checkAuthAndSync()
  }, [navigate, syncPreferencesToLocalStorage])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    try {
      setLoading(true)
      setError(null)

      console.log("[Login] Iniciando processo de login...")

      const trimmedEmail = email.trim()
      const { authData } = await login(trimmedEmail, password)
      const user_id = authData.user?.id

      if (!user_id) {
        setError("Não foi possível identificar o usuário.")
        setLoading(false)
        return
      }

      console.log(
        "[Login] Login bem-sucedido, identificando tipo de usuário..."
      )

      // Salva dados básicos do usuário primeiro
      await saveToStorage("userEmail", trimmedEmail)
      await saveToStorage("userId", user_id)

      // Salva o token de acesso se disponível
      if (authData.session?.access_token) {
        await saveToStorage("accessToken", authData.session.access_token)
      }
      if (authData.session?.refresh_token) {
        await saveToStorage("refreshToken", authData.session.refresh_token)
      }

      // Verifica tipo de usuário e salva informações específicas
      const isCliente = await existsInCliente(user_id)
      if (isCliente) {
        console.log("[Login] Usuário identificado como cliente")
        const cliente = await getClienteByUserId(user_id)

        // Verifica se o cliente está ativo
        if (!isUserActive(cliente, "cliente")) {
          console.log("[Login] Cliente não está ativo")
          setError(
            "Sua conta não está ativa. Entre em contato com o suporte para ativar sua conta."
          )
          await clearAllStorage()
          setLoading(false)
          return
        }

        await saveToStorage("userType", "cliente")
        if (cliente && cliente.id) {
          await saveToStorage("clienteId", cliente.id.toString())
          console.log("[Login] ClienteId salvo nos storages")
        }

        // Salvar o plano e redirecionar para /home
        const plano = cliente?.tipo_plano ?? ""
        await saveToStorage("tipoPlano", plano)

        navigate("/home")
        return
      }

      const isSocorrista = await existsInSocorrista(user_id)
      if (isSocorrista) {
        console.log("[Login] Usuário identificado como socorrista")
        const socorrista = await getSocorristaByUserId(user_id)

        // Verifica se o socorrista está ativo
        if (!isUserActive(socorrista, "socorrista")) {
          console.log("[Login] Socorrista não está ativo")
          setError(
            "Sua conta não está ativa. Entre em contato com o suporte para ativar sua conta."
          )
          await clearAllStorage()
          setLoading(false)
          return
        }

        await saveToStorage("userType", "socorrista")
        if (socorrista && socorrista.id) {
          await saveToStorage("socorristaId", socorrista.id.toString())
          console.log("[Login] SocorristaId salvo nos storages")
        }
        navigate("/partner-emergencies")
        return
      }

      const isAdministrador = await existsInAdministrador(user_id)
      if (isAdministrador) {
        console.log("[Login] Usuário identificado como administrador")
        const administrador = await getAdministradorByUserId(user_id)

        // Verifica se o administrador está ativo
        if (!isUserActive(administrador, "administrador")) {
          console.log("[Login] Administrador não está ativo")
          setError(
            "Sua conta não está ativa. Entre em contato com o suporte para ativar sua conta."
          )
          await clearAllStorage()
          setLoading(false)
          return
        }

        await saveToStorage("userType", "administrador")
        if (administrador && administrador.id) {
          await saveToStorage("administradorId", administrador.id.toString())
          console.log("[Login] AdministradorId salvo nos storages")
        }
        navigate("/admin-panel")
        return
      }

      const isFamiliar = await existsInFamiliar(user_id)
      if (isFamiliar) {
        console.log("[Login] Usuário identificado como familiar")
        await saveToStorage("userType", "familiar")
        const familiar = await getFamiliarByUserId(user_id)
        if (familiar && familiar.id) {
          await saveToStorage("familiarId", familiar.id.toString())
          // Salvar também o clienteId do familiar para permitir busca por cliente_id
          if (familiar.cliente_id) {
            await saveToStorage("clienteId", familiar.cliente_id.toString())
            console.log("[Login] ClienteId do familiar salvo nos storages")
          }
          console.log("[Login] FamiliarId salvo nos storages")
        }
        navigate("/family-emergencies")
        return
      }

      const isDependente = await existsInDependente(user_id)
      if (isDependente) {
        console.log("[Login] Usuário identificado como dependente")
        const dependente = await getDependenteByUserId(user_id)

        // Verifica se o dependente está ativo
        if (!isUserActive(dependente, "dependente")) {
          console.log("[Login] Dependente não está ativo")
          setError(
            "Sua conta não está ativa. Entre em contato com o suporte para ativar sua conta."
          )
          await clearAllStorage()
          setLoading(false)
          return
        }

        await saveToStorage("userType", "dependente")
        if (dependente && dependente.id) {
          await saveToStorage("dependenteId", dependente.id.toString())
          // Salva o clienteId do titular para a carteirinha
          if (dependente.cliente_id) {
            await saveToStorage("clienteId", dependente.cliente_id.toString())
          }
          console.log("[Login] DependenteId salvo nos storages")
        }
        navigate("/carteirinha")
        return
      }

      const isMedico = await existsInMedicos(user_id)
      if (isMedico) {
        console.log("[Login] Usuário identificado como médico")
        const medico = await getMedicoByUserId(user_id)

        // Verifica se o médico está ativo
        if (!isUserActive(medico, "medico")) {
          console.log("[Login] Médico não está ativo")
          setError(
            "Sua conta não está ativa. Entre em contato com o suporte para ativar sua conta."
          )
          await clearAllStorage()
          setLoading(false)
          return
        }

        await saveToStorage("userType", "medico")
        if (medico && medico.id) {
          await saveToStorage("medicoId", medico.id.toString())
          console.log("[Login] MedicoId salvo nos storages")
        }
        navigate("/telemedicina/medico")
        return
      }

      console.log("[Login] Usuário não encontrado em nenhuma tabela específica")
      setError("Usuário não autorizado. Entre em contato com o suporte.")
      await clearAllStorage()
    } catch (error) {
      console.error("[Login] Erro ao realizar login:", error)
      setError("Email ou senha incorretos. Por favor, tente novamente.")
      await clearAllStorage()
    } finally {
      setLoading(false)
    }
  }

  const handleRecoverPassword = async () => {
    try {
      setLoading(true)
      setError(null)

      console.log("[Login] Iniciando recuperação de senha para:", email)
      await recoverPassword(email)
      alert("Instruções de recuperação de senha enviadas para o seu email.")
    } catch (error) {
      console.error("[Login] Erro na recuperação de senha:", error)
      setError(
        "Erro ao enviar instruções de recuperação de senha. Por favor, tente novamente."
      )
    } finally {
      setLoading(false)
    }
  }

  const handleSplashFinish = () => {
    setShowSplash(false)
  }

  // Se ainda está verificando autenticação, mostra splash
  if (isCheckingAuth) {
    return <SplashScreen onFinish={handleSplashFinish} duration={2000} />
  }

  // Se não está verificando e ainda está mostrando splash, renderiza apenas ela
  if (showSplash) {
    return <SplashScreen onFinish={handleSplashFinish} duration={3000} />
  }

  return (
    <Container
      maxWidth='sm'
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        minHeight: "100vh",
        pt: 4
      }}
    >
      <img
        src={Logo}
        alt='Logo'
        style={{ width: "256px", height: "224px", marginBottom: "20px" }}
      />
      <Typography variant='h5' component='h2' gutterBottom>
        {showLogin ? "Login" : "Recuperar Senha"}
      </Typography>

      {error && (
        <Typography color='error' sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {/* Debug info - só aparece em desenvolvimento */}
      {process.env.NODE_ENV === "development" && (
        <Box
          sx={{
            mb: 2,
            p: 1,
            backgroundColor: "#f5f5f5",
            borderRadius: 1,
            width: "100%"
          }}
        >
          <Typography variant='caption' color='text.secondary'>
            Debug: Plataforma -{" "}
            {Capacitor.isNativePlatform() ? "Mobile Nativo" : "Web"}
          </Typography>
        </Box>
      )}

      {showLogin ? (
        <Box
          component='form'
          onSubmit={handleSubmit}
          sx={{ width: "100%", mt: 2 }}
        >
          <TextField
            label='Email'
            type='email'
            fullWidth
            margin='dense'
            variant='outlined'
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={loading}
            required
          />
          <TextField
            label='Senha'
            type='password'
            fullWidth
            margin='dense'
            variant='outlined'
            value={password}
            onChange={e => setPassword(e.target.value)}
            disabled={loading}
            required
          />
          <Button
            type='submit'
            variant='contained'
            color='primary'
            fullWidth
            sx={{ mt: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Entrar"}
          </Button>
          <Button
            onClick={() => setShowLogin(false)}
            variant='text'
            color='primary'
            fullWidth
            sx={{ mt: 1 }}
            disabled={loading}
          >
            Esqueceu a senha?
          </Button>
        </Box>
      ) : (
        <Box sx={{ mt: 2, width: "100%" }}>
          <TextField
            label='Email para recuperação'
            type='email'
            fullWidth
            margin='dense'
            variant='outlined'
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={loading}
            required
          />
          <Button
            onClick={handleRecoverPassword}
            variant='contained'
            color='primary'
            fullWidth
            sx={{ mt: 1 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Enviar Instruções"}
          </Button>
          <Button
            onClick={() => setShowLogin(true)}
            variant='text'
            color='primary'
            fullWidth
            sx={{ mt: 1 }}
            disabled={loading}
          >
            Voltar ao Login
          </Button>
        </Box>
      )}
      <Box>
        <Typography
          variant='body2'
          align="center"
        >
          Ao efetuar login, voce concorda com os nossos
          <Button
            variant="text"
            size="small"
            onClick={() => Browser.open({ url: 'https://dotzion.com.br/politicas/supportlife24h/eula.html' })}
          >EULA</Button>e
          <Button
            variant="text"
            size="small"
            onClick={() => Browser.open({ url: 'https://dotzion.com.br/politicas/supportlife24h' })}
          >Política de Privacidade</Button>.
        </Typography>
      </Box>
    </Container>
  )
}

export default Login
